import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Loader2, Ruler, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { UNITS, MATERIALS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MediaUploader from "@/components/MediaUploader";
import MediaGrid from "@/components/MediaGrid";

const today = () => new Date().toISOString().slice(0, 10);
export const EMPTY_QTY = { position_id: "", service: "", material: "", amount: "", unit: "Stück", date: today(), company: "", employee: "", note: "" };

export function QuantityForm({ form, setForm, positions, onSave, busy, onCancel, hidePosition = false }) {
  const on = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(); }} className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50" data-testid="quantity-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <div><Label className="text-xs">Leistung *</Label><Input value={form.service} onChange={on("service")} placeholder="z.B. Betonage Decke" data-testid="qty-service-input" /></div>
        <div><Label className="text-xs">Material</Label>
          <Select value={form.material || "none"} onValueChange={(v) => setForm({ ...form, material: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="qty-material-select"><SelectValue placeholder="Material" /></SelectTrigger>
            <SelectContent><SelectItem value="none">—</SelectItem>{MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><Label className="text-xs">Menge *</Label><Input type="number" step="any" value={form.amount} onChange={on("amount")} data-testid="qty-amount-input" /></div>
        <div><Label className="text-xs">Einheit</Label><Select value={form.unit} onValueChange={on("unit")}><SelectTrigger data-testid="qty-unit-select"><SelectValue /></SelectTrigger><SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-xs">Datum</Label><Input type="date" value={form.date} onChange={on("date")} data-testid="qty-date-input" /></div>
      </div>
      <div className={`grid ${hidePosition ? "sm:grid-cols-2" : "sm:grid-cols-3"} gap-2`}>
        <div><Label className="text-xs">Ausführende Firma</Label><Input value={form.company} onChange={on("company")} data-testid="qty-company-input" /></div>
        <div><Label className="text-xs">Mitarbeiter</Label><Input value={form.employee} onChange={on("employee")} data-testid="qty-employee-input" /></div>
        {!hidePosition && (
          <div><Label className="text-xs">Bauplanposition</Label>
            <Select value={form.position_id || "none"} onValueChange={(v) => setForm({ ...form, position_id: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="qty-position-select"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Keine Position</SelectItem>{positions.map((p) => <SelectItem key={p.id} value={p.id}>{p.number} {p.title ? `· ${p.title}` : ""}</SelectItem>)}</SelectContent>
            </Select></div>
        )}
      </div>
      <Textarea placeholder="Bemerkung…" value={form.note} onChange={on("note")} rows={2} data-testid="qty-note-input" />
      <div className="flex gap-2">
        <Button type="submit" disabled={busy} className="flex-1" data-testid="save-qty-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mengenposition speichern"}</Button>
        {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>Abbrechen</Button>}
      </div>
    </form>
  );
}

export function QuantityRow({ q, onChanged, showPosition = true }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const loadPhotos = useCallback(() => api.get(`/media?quantity_id=${q.id}`).then((r) => setPhotos(r.data)), [q.id]);
  useEffect(() => { if (open) loadPhotos(); }, [open, loadPhotos]);
  const del = async () => { await api.delete(`/quantities/${q.id}`); onChanged(); };
  return (
    <div className="border border-slate-200 rounded-xl p-3" data-testid={`qty-item-${q.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-slate-900">{q.service || q.description}</span>
            {q.material && <span className="text-xs bg-slate-100 rounded px-1.5 py-0.5">{q.material}</span>}
            {showPosition && q.position_number && <span className="font-mono-tech text-[10px] text-primary bg-primary/10 px-1.5 py-0.5 rounded">{q.position_number}</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1 flex gap-3 flex-wrap">
            <span>📅 {q.date}</span>{q.company && <span>🏢 {q.company}</span>}{q.employee && <span>👤 {q.employee}</span>}
          </div>
          {q.note && <p className="text-xs text-slate-500 mt-1">{q.note}</p>}
        </div>
        <div className="font-mono-tech font-bold text-slate-900 whitespace-nowrap">{q.amount} {q.unit}</div>
        <button onClick={() => setOpen(!open)} className="text-slate-400 hover:text-primary" title="Fotos" data-testid={`qty-photos-toggle-${q.id}`}><Camera className="h-4 w-4" />{(q.photo_count || photos.length) > 0 && <span className="text-[10px]">{photos.length || q.photo_count}</span>}</button>
        <button onClick={del} className="text-slate-300 hover:text-red-500" data-testid={`delete-qty-${q.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
      {open && (
        <div className="mt-3 space-y-2">
          <MediaUploader links={{ quantity_id: q.id }} onUploaded={() => { loadPhotos(); onChanged(); }} compact />
          {photos.length > 0 && <MediaGrid photos={photos} onChanged={loadPhotos} cols="grid-cols-4 sm:grid-cols-6" />}
        </div>
      )}
    </div>
  );
}

export default function QuantitiesPanel({ projectId }) {
  const [items, setItems] = useState([]);
  const [positions, setPositions] = useState([]);
  const [form, setForm] = useState(EMPTY_QTY);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dateFilter, setDateFilter] = useState("");

  const load = useCallback(() => api.get(`/projects/${projectId}/quantities${dateFilter ? `?date_=${dateFilter}` : ""}`).then((r) => setItems(r.data)), [projectId, dateFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/search?q=POS&kind=positions&project_id=${projectId}`).then((r) => setPositions(r.data.positions)); }, [projectId]);

  const save = async () => {
    if (!form.service || form.amount === "") return toast.error("Leistung und Menge erforderlich");
    setBusy(true);
    try { await api.post(`/projects/${projectId}/quantities`, { ...form, amount: parseFloat(form.amount) || 0, position_id: form.position_id || null }); toast.success("Mengenposition erfasst"); setForm({ ...EMPTY_QTY, date: form.date, company: form.company, employee: form.employee }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const byDate = items.reduce((acc, q) => { (acc[q.date] = acc[q.date] || []).push(q); return acc; }, {});
  const sums = items.reduce((acc, q) => { acc[q.unit] = (acc[q.unit] || 0) + (q.amount || 0); return acc; }, {});

  return (
    <div className="space-y-4" data-testid="quantities-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-head font-bold flex items-center gap-2"><Ruler className="h-5 w-5 text-primary" />Mengenberichte <span className="text-sm text-slate-400 font-normal">({items.length})</span></h3>
        <div className="flex items-center gap-2">
          <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="h-9 w-40" data-testid="qty-filter-date" />
          {dateFilter && <Button variant="ghost" size="sm" onClick={() => setDateFilter("")}>Alle</Button>}
          {!adding && <Button size="sm" onClick={() => setAdding(true)} data-testid="add-quantity-btn"><Plus className="h-4 w-4 mr-1" />Menge erfassen</Button>}
        </div>
      </div>
      {adding && <QuantityForm form={form} setForm={setForm} positions={positions} onSave={save} busy={busy} onCancel={() => setAdding(false)} />}
      {Object.keys(sums).length > 0 && <div className="flex gap-2 flex-wrap text-xs">{Object.entries(sums).map(([u, v]) => <span key={u} className="bg-slate-100 rounded-full px-2.5 py-1 font-mono-tech">{Math.round(v * 100) / 100} {u}</span>)}</div>}
      {items.length === 0 && <Card className="p-8 text-center text-slate-400">Noch keine Mengen erfasst.</Card>}
      {Object.entries(byDate).map(([d, list]) => (
        <div key={d} className="space-y-2">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{d} · Tagesbericht ({list.length} Positionen)</div>
          {list.map((q) => <QuantityRow key={q.id} q={q} onChanged={load} />)}
        </div>
      ))}
    </div>
  );
}
